import React from 'react';
import { useTranslation } from 'next-i18next';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DraftsIcon from '@mui/icons-material/Drafts';
import { DocumentStatus } from 'src/@types/ClinicDocuments';
import { CalendarIcon } from 'src/components/icons/Calendar';
import { CostPlanIcon } from 'src/components/icons/CostPlan';
import { DentalIq } from 'src/components/icons/DentalIq';
import { InvoicesIcon } from 'src/components/icons/Invoices';
import { MedicalInfo } from 'src/components/icons/MedicalInfo';
import { Practitioner } from 'src/components/icons/Practitioner';
import { MenuIndicator, MenuIndicatorKind } from 'src/components/MenuIndicator/MenuIndicator';
import { setDefaultBookingContext, useBookingsDispatch } from 'src/components/providers/BookingsProvider';
import { isApplicable } from 'src/screens/Documents/helpers';
import { useClinicDocuments } from './api/useClinicDocuments';
import { useCostPlanEvaluation } from './useCostplanEvaluation';
import { useInvoiceEvaluation } from './useInvoiceEvaluation';
import { usePractitionerEvaluation } from './usePractitionersEvaluation';
export const useMenu = () => {
  const {
    t
  } = useTranslation();
  const dispatch = useBookingsDispatch();
  const {
    data: clinicDocuments
  } = useClinicDocuments({});
  const invoiceEvaluation = useInvoiceEvaluation({});
  const practitionersEvaluation = usePractitionerEvaluation({});
  const costPlanEvaluation = useCostPlanEvaluation({});
  const documentsNeedingAttention = (clinicDocuments ?? []).reduce((sum, entry) => sum + entry.anamnesisState.filter(doc => isApplicable(doc) && doc.status !== DocumentStatus.COMPLETE).length, 0);
  const conditionalItem = (condition, item) => {
    return condition ? [item] : [];
  };
  return [{
    label: t('b2c.menu.label.medicalData'),
    children: [{
      label: 'Dental IQ',
      href: '/dental-iq',
      Icon: /*#__PURE__*/React.createElement(DentalIq, null)
    }, {
      label: t('b2c.menu.medicalHistory'),
      href: '/record',
      Icon: /*#__PURE__*/React.createElement(MedicalInfo, null)
    }, {
      label: t('b2c.menu.appointments'),
      href: '/appointments',
      Icon: /*#__PURE__*/React.createElement(CalendarIcon, null),
      effect: () => {
        dispatch(setDefaultBookingContext());
      }
    }, {
      content: /*#__PURE__*/React.createElement(MenuIndicator, {
        kind: MenuIndicatorKind.Warning,
        count: costPlanEvaluation.costPlanRecommendation ? 1 : 0
      }, t('b2c.menu.costplans')),
      href: costPlanEvaluation.costPlanRecommendation ? '/costplan' : '/costplan/all',
      Icon: /*#__PURE__*/React.createElement(CostPlanIcon, null)
    }, {
      content: /*#__PURE__*/React.createElement(MenuIndicator, {
        kind: invoiceEvaluation.outdated.length > 0 ? MenuIndicatorKind.Error : MenuIndicatorKind.Warning,
        count: invoiceEvaluation.open.length + invoiceEvaluation.outdated.length
      }, t('b2c.menu.invoices')),
      href: '/invoice',
      Icon: /*#__PURE__*/React.createElement(InvoicesIcon, null)
    }, ...conditionalItem(practitionersEvaluation?.all?.length > 0, {
      label: t('b2c.menu.practitioners'),
      href: `/practitioners/${practitionersEvaluation.favourite.length > 0 ? '' : 'all'}`,
      Icon: /*#__PURE__*/React.createElement(Practitioner, null)
    }), {
      content: /*#__PURE__*/React.createElement(MenuIndicator, {
        kind: documentsNeedingAttention > 0 ? MenuIndicatorKind.Error : MenuIndicatorKind.Warning,
        count: documentsNeedingAttention
      }, t('b2c.menu.documents')),
      href: '/documents',
      Icon: /*#__PURE__*/React.createElement(DescriptionOutlinedIcon, null)
    }]
  }, {
    label: t('b2c.menu.label.help'),
    children: [{
      label: t('b2c.menu.contact'),
      href: '/contact',
      Icon: /*#__PURE__*/React.createElement(DraftsIcon, null)
    }]
  }];
};